// Sineta de notificações operacionais
// - Polling a cada 60s do /v1/notificacoes/resumo
// - Badge com total de itens pendentes
// - Click abre dropdown com 4 seções: Ações IA, Visitas atrasadas, Alertas de veículo, Veículos offline

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificacoes } from '../lib/api.js';

const INTERVALO_MS = 60_000;

export default function SinetaNotificacoes() {
  const [resumo, setResumo] = useState(null);
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  async function carregar() {
    try {
      const r = await notificacoes.resumo();
      setResumo(r);
    } catch { /* silencioso — sineta não pode quebrar a página */ }
  }

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, INTERVALO_MS);
    return () => clearInterval(t);
  }, []);

  // Fecha ao clicar fora
  useEffect(() => {
    function onDoc(e) {
      if (aberto && ref.current && !ref.current.contains(e.target)) setAberto(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [aberto]);

  const total = resumo?.total || 0;
  const semNada = total === 0;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className="bell" onClick={() => setAberto(v => !v)}
        title={semNada ? 'Sem novidades' : `${total} item(ns) requerem atenção`}
        style={{ cursor: 'pointer', userSelect: 'none' }}>
        🔔
        {!semNada && <div className="bell-badge">{total > 99 ? '99+' : total}</div>}
      </div>

      {aberto && (
        <div style={painel}>
          <div style={cabecalho}>
            <strong>Notificações</strong>
            {!semNada && <span style={{ color: '#6b7280', fontSize: 11 }}>{total} pendente(s)</span>}
          </div>

          {semNada ? (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
              ✅ Nada precisando de atenção agora.
            </div>
          ) : (
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>

              {/* Ações IA */}
              {resumo.acoes_ia.total > 0 && (
                <Secao titulo="✨ Ações IA aguardando aprovação"
                  total={resumo.acoes_ia.total}
                  cor="#3b82f6"
                  onIrPara={() => { nav('/acoes'); setAberto(false); }}>
                  {resumo.acoes_ia.itens.map(a => (
                    <Item key={a.id}
                      icone={iconeSeveridade(a.severidade)}
                      titulo={tituloAcao(a.tipo, a.cliente, a.tecnico)}
                      desc={a.motivo}
                      ts={a.ts}
                      onClick={() => { nav('/acoes'); setAberto(false); }}/>
                  ))}
                </Secao>
              )}

              {/* Visitas atrasadas */}
              {resumo.visitas_atrasadas.total > 0 && (
                <Secao titulo="⏰ Visitas atrasadas"
                  total={resumo.visitas_atrasadas.total}
                  cor="#dc2626"
                  onIrPara={() => { nav('/agenda'); setAberto(false); }}>
                  {resumo.visitas_atrasadas.itens.map(v => (
                    <Item key={v.id}
                      icone="⏰"
                      titulo={`${v.cliente} (${v.tecnico || '?'})`}
                      desc={`${v.status} · atrasada ${v.min_atraso} min`}
                      onClick={() => { nav(`/visitas/${v.id}`); setAberto(false); }}/>
                  ))}
                </Secao>
              )}

              {/* Alertas de veículo */}
              {resumo.alertas_veiculo.total > 0 && (
                <Secao titulo="🚨 Alertas de veículo"
                  total={resumo.alertas_veiculo.total}
                  cor="#ea580c">
                  {resumo.alertas_veiculo.itens.map(a => (
                    <Item key={a.id}
                      icone="🚨"
                      titulo={`${a.placa} · ${a.tipo}`}
                      desc={`${a.tecnico || '?'} — ${a.descricao || 'sem detalhes'}`}
                      ts={a.ts}/>
                  ))}
                </Secao>
              )}

              {/* Veículos offline */}
              {resumo.veiculos_offline.total > 0 && (
                <Secao titulo="📡 Veículos sem GPS recente"
                  total={resumo.veiculos_offline.total}
                  cor="#9ca3af">
                  {resumo.veiculos_offline.itens.map(v => (
                    <Item key={v.id}
                      icone="📡"
                      titulo={`${v.placa} · ${v.tecnico || '?'}`}
                      desc={`Última posição há ${v.min_silencio} min`}/>
                  ))}
                </Secao>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Secao({ titulo, total, cor, onIrPara, children }) {
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
      <div style={{
        padding: '8px 14px', background: '#fafafa',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
        color: cor,
      }}>
        <span>{titulo} · {total}</span>
        {onIrPara && (
          <button onClick={onIrPara} style={{
            background: 'none', border: 'none', color: cor, cursor: 'pointer',
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px',
          }}>Ver →</button>
        )}
      </div>
      {children}
    </div>
  );
}

function Item({ icone, titulo, desc, ts, onClick }) {
  return (
    <div onClick={onClick}
      style={{
        padding: '8px 14px', display: 'flex', gap: 10,
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: '1px solid #f9fafb',
        alignItems: 'flex-start',
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background = '#f9fafb')}
      onMouseLeave={e => onClick && (e.currentTarget.style.background = 'transparent')}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>{icone}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {titulo}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {desc}
        </div>
        {ts && (
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
            {timeAgo(ts)}
          </div>
        )}
      </div>
    </div>
  );
}

function iconeSeveridade(sev) {
  switch (sev) {
    case 'critica': return '🚨';
    case 'alta': return '⚠️';
    case 'media': return '💡';
    default: return 'ℹ️';
  }
}

function tituloAcao(tipo, cliente, tecnico) {
  const map = {
    notificar_atraso_proximo_cliente: 'Avisar próximo cliente',
    reagendar_visita_para_outro_slot: 'Reagendar visita',
    realocar_visita_para_outro_tecnico: 'Realocar técnico',
    cancelar_visita_por_inviabilidade: 'Cancelar visita',
    enviar_link_tracking_cliente: 'Enviar tracking',
    apenas_observacao: 'Observação',
  };
  const t = map[tipo] || tipo;
  if (cliente) return `${t} · ${cliente}`;
  if (tecnico) return `${t} · ${tecnico}`;
  return t;
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff/60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h atrás`;
  return `${Math.floor(diff/86400)} dia(s) atrás`;
}

const painel = {
  position: 'absolute', right: 0, top: 'calc(100% + 8px)',
  background: '#fff',
  border: '1px solid var(--cinza-borda)',
  borderRadius: 10,
  boxShadow: 'var(--shadow-md)',
  width: 380, maxWidth: '92vw',
  zIndex: 200, overflow: 'hidden',
};
const cabecalho = {
  padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#fff',
};
